import {
    Text,
    VStack,
    HStack,
    Checkbox,
    Box,
    Icon,
    IconButton,
    Tr,
    Switch,
    Select,
} from '@chakra-ui/react';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TbCar, TbEdit, TbMapPin, TbTrash } from 'react-icons/tb';
import { QuotingSettings, QuoteRequirement, OEMSettings } from '../../hooks/useGetSettings';
import { useSettingsDraft } from '../../pages/settings/SettingsDraftContext';
import { useGetLocations } from '../../hooks/useGetLocations';
import PartBayButton from '../PartBayButton';
import BulkActionBar, { type BulkActionBarItem } from '../BulkActionBar';
import OemOverridesTable, { type OemOverrideColumn } from './OemOverridesTable';
import RequoteBulkEditModal, {
    type RequoteBulkField,
    type RequoteBulkValue,
} from './RequoteBulkEditModal';
import { InlineMakesCell } from './MakesInlineCells';
import { InlineLocationListCell } from './InsuranceInlineCells';
import {
    SettingsCard,
    SettingsNumberField,
    SettingsRow,
    SettingsSegmentedControl,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from './primitives';
import { PARTBAY_BLUE } from './primitives/constants';

type QuotingMarketplace = keyof QuotingSettings['timeBounds'];

const MARKETPLACE_LABELS: Record<QuotingMarketplace, string> = {
    collisionLink: 'CollisionLink',
    partstrader: 'PartsTrader',
};

/**
 * One column: a make's floor on the quoting window. It overrides the minimum
 * for both marketplaces, so it sits under the window rather than beside a
 * single marketplace's pair of bounds.
 */
const MIN_QUOTING_TIME_COLUMNS: Array<OemOverrideColumn> = [
    { field: 'minQuotingTime', label: 'Minimum quoting time', scale: 1, min: 20, max: 180, unit: 'min' },
];

/** What the bulk bar can change across a selection of re-quote rules. */
const BULK_RULE_ACTIONS: BulkActionBarItem[] = [
    { id: 'locations', label: 'Locations', icon: TbMapPin as React.ElementType },
    { id: 'makes', label: 'Makes', icon: TbCar as React.ElementType },
    { id: 'remove', label: 'Remove', icon: TbTrash as React.ElementType, danger: true },
];

const SKIP_QUOTING_OPTIONS = [
    { value: 'always', label: 'Always' },
    { value: 're-quote', label: 'Re-quote' },
    { value: 'never', label: 'Never' },
] as const;

// const REQUOTE_OPTIONS = [
//     {
//         value: 'all',
//         label: 'All',
//         tooltip: 'Always correct and re-quote an incorrect part number.',
//     },
//     {
//         value: 'partial',
//         label: 'Partial',
//         tooltip: 'Only re-quote when price matching or when a non-OEM part was auto-selected.',
//     },
//     {
//         value: 'off',
//         label: 'Off',
//         tooltip: 'Never automatically correct or re-quote an incorrect part number.',
//     },
// ] as const;

interface QuotingInterface {
    data: QuotingSettings;
    hasCl: boolean;
    hasPt: boolean;
    oemOptions: Array<{
        _id: string;
        name: string;
    }>;
    /** The oem subdocument, which stores the per-make minimum quoting times. */
    oems: Array<OEMSettings>;
}

const Quoting = ({ data, hasCl, hasPt, oemOptions, oems }: QuotingInterface) => {
    // Edits are buffered on the shared settings draft, so the page's save bar
    // writes the quoting and oem changes together in one request.
    const { getDomain, setDomain, isDirty } = useSettingsDraft();
    const localData = getDomain('quoting', data) ?? data;
    const localOems = getDomain('oem', oems) ?? oems;
    const navigate = useNavigate();
    const { data: locationsData } = useGetLocations(true);
    const locations = useMemo(() => locationsData?.locations ?? [], [locationsData]);

    // Re-quote rules are keyed by their position in the array, which is what
    // the rule's own screen is routed by; a removal clears the selection rather
    // than trying to follow the rows that shifted under it.
    const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
    const [bulkField, setBulkField] = useState<RequoteBulkField | null>(null);

    const requoteRules = useMemo(() => localData.requote ?? [], [localData.requote]);
    const ruleRows = useMemo(
        () => requoteRules.map((rule, index) => ({ rule, index })),
        [requoteRules],
    );
    const rulePaging = useSubTablePagination(ruleRows);

    // Rules removed elsewhere (the rule's own screen, a discard) must not leave
    // the bar acting on positions that no longer exist.
    useEffect(() => {
        setSelectedIndexes((prev) => {
            const next = new Set(Array.from(prev).filter((index) => index < requoteRules.length));
            return next.size === prev.size ? prev : next;
        });
    }, [requoteRules.length]);

    const visibleMarketplaces = useMemo<Array<QuotingMarketplace>>(() => {
        const marketplaces: Array<QuotingMarketplace> = [];
        if (hasCl) marketplaces.push('collisionLink');
        if (hasPt) marketplaces.push('partstrader');
        return marketplaces;
    }, [hasCl, hasPt]);

    const handleUpdatePreference = (field: keyof QuotingSettings, value: boolean | number | string | Array<QuoteRequirement>) => {
        const sanitizedValue = typeof value === 'number' && isNaN(value) ? 0 : value;
        setDomain('quoting', { ...localData, [field]: sanitizedValue });
    };

    const handleTimeBoundChange = (
        marketplace: QuotingMarketplace,
        bound: 'min' | 'max',
        value: number,
    ) => {
        const current = localData.timeBounds[marketplace];
        const sanitizedValue = isNaN(value) ? current[bound] : value;
        const next = { ...current, [bound]: sanitizedValue };

        // Keep the window valid regardless of which end the user edited
        if (bound === 'min' && next.max < next.min) next.max = next.min;
        if (bound === 'max' && next.max < next.min) next.max = next.min;

        setDomain('quoting', {
            ...localData,
            timeBounds: { ...localData.timeBounds, [marketplace]: next },
        });
    };

    // Per-make minimums live on the oem subdocument; the window they override
    // lives here, so this section writes both.
    const handleOemsChange = (next: Array<OEMSettings>) => {
        setDomain('oem', next);
    };

    const handleSkipQuotingChange = (
        job: 'drp' | 'nonDrp',
        value: 'always' | 're-quote' | 'never',
    ) => {
        setDomain('quoting', {
            ...localData,
            skipQuoting: {
                drp: localData.skipQuoting?.drp ?? 'never',
                nonDrp: localData.skipQuoting?.nonDrp ?? 'never',
                [job]: value,
            },
        });
    };

    const handleCutoffChange = (field: 'hour' | 'minute', value: number) => {
        setDomain('quoting', { ...localData, cutoff: { ...localData.cutoff, [field]: value } });
    };

    const handleRuleChange = (index: number, patch: Partial<QuoteRequirement>) => {
        handleUpdatePreference(
            'requote',
            requoteRules.map((rule, current) => (current === index ? { ...rule, ...patch } : rule)),
        );
    };

    const handleRemoveRule = (index: number) => {
        handleUpdatePreference(
            'requote',
            requoteRules.filter((_, current) => current !== index),
        );
        setSelectedIndexes(new Set());
    };

    const handleToggleSelect = (index: number, checked: boolean) => {
        setSelectedIndexes((prev) => {
            const next = new Set(prev);
            if (checked) next.add(index);
            else next.delete(index);
            return next;
        });
    };

    const handleSelectAllOnPage = (checked: boolean) => {
        setSelectedIndexes((prev) => {
            const next = new Set(prev);
            rulePaging.pageItems.forEach(({ index }) => {
                if (checked) next.add(index);
                else next.delete(index);
            });
            return next;
        });
    };

    const handleBulkApply = (value: RequoteBulkValue) => {
        handleUpdatePreference(
            'requote',
            requoteRules.map((rule, index) =>
                selectedIndexes.has(index)
                    ? {
                        ...rule,
                        ...(value.makes ? { makes: value.makes } : {}),
                        ...(value.locations !== undefined ? { locations: value.locations } : {}),
                    }
                    : rule,
            ),
        );
        setBulkField(null);
    };

    const handleBulkRemove = () => {
        handleUpdatePreference(
            'requote',
            requoteRules.filter((_, index) => !selectedIndexes.has(index)),
        );
        setSelectedIndexes(new Set());
    };

    const handleBulkAction = (actionId: string) => {
        if (actionId === 'remove') {
            handleBulkRemove();
            return;
        }
        setBulkField(actionId as RequoteBulkField);
    };

    const pageIndexes = rulePaging.pageItems.map(({ index }) => index);
    const allPageSelected =
        pageIndexes.length > 0 && pageIndexes.every((index) => selectedIndexes.has(index));
    const somePageSelected = pageIndexes.some((index) => selectedIndexes.has(index));

    return (
        <VStack spacing={6} align="stretch" pb={16}>
            <SettingsCard
                title="Quoting window"
                description="How long suppliers are given to respond with quotes before selections are made, in minutes."
            >
                {visibleMarketplaces.map((marketplace) => (
                    <SettingsRow
                        key={marketplace}
                        label={MARKETPLACE_LABELS[marketplace]}
                        description={`Shortest and longest quoting window Yaxle may use on ${MARKETPLACE_LABELS[marketplace]}.`}
                        control={
                            <HStack spacing={4} align="center">
                                <HStack spacing={2} align="center">
                                    <Text fontSize="xs" color="gray.500">
                                        Min
                                    </Text>
                                    <SettingsNumberField
                                        value={localData.timeBounds[marketplace].min}
                                        onChange={(next) =>
                                            handleTimeBoundChange(marketplace, 'min', next)
                                        }
                                        allowEmpty={false}
                                        min={20}
                                        max={180}
                                        width="80px"
                                        ariaLabel={`${MARKETPLACE_LABELS[marketplace]} minimum quoting window`}
                                    />
                                </HStack>
                                <HStack spacing={2} align="center">
                                    <Text fontSize="xs" color="gray.500">
                                        Max
                                    </Text>
                                    <SettingsNumberField
                                        value={localData.timeBounds[marketplace].max}
                                        onChange={(next) =>
                                            handleTimeBoundChange(marketplace, 'max', next)
                                        }
                                        allowEmpty={false}
                                        min={localData.timeBounds[marketplace].min}
                                        max={marketplace === 'collisionLink' ? 360 : 180}
                                        width="80px"
                                        ariaLabel={`${MARKETPLACE_LABELS[marketplace]} maximum quoting window`}
                                    />
                                </HStack>
                            </HStack>
                        }
                    />
                ))}

                <OemOverridesTable
                    title="Minimum quoting time by make (optional)"
                    description="Raise the minimum quoting time by make for all marketplaces."
                    columns={MIN_QUOTING_TIME_COLUMNS}
                    oems={localOems}
                    oemOptions={oemOptions}
                    onChange={handleOemsChange}
                    emptyLabel="No per-make minimums configured. Every make uses the marketplace quoting window above."
                    modalTitle="Add Makes"
                />
            </SettingsCard>

            <SettingsCard
                title="Quoting preferences"
                description="Configure timing and behavior for quoting processes."
            >
                <SettingsRow
                    label="Skip quoting (DRP)"
                    description="Enable skip quoting on DRP jobs."
                    control={
                        <SettingsSegmentedControl
                            segments={SKIP_QUOTING_OPTIONS}
                            value={localData.skipQuoting?.drp ?? 'never'}
                            onChange={(value) => handleSkipQuotingChange('drp', value)}
                        />
                    }
                />

                <SettingsRow
                    label="Skip quoting (non-DRP)"
                    description="Enable skip quoting on non-DRP jobs."
                    control={
                        <SettingsSegmentedControl
                            segments={SKIP_QUOTING_OPTIONS}
                            value={localData.skipQuoting?.nonDrp ?? 'never'}
                            onChange={(value) => handleSkipQuotingChange('nonDrp', value)}
                        />
                    }
                />

                <SettingsRow
                    label="Auto quote stale orders"
                    description="For DRP jobs that have been open for a week, and have not been quoted."
                    control={
                        <Switch
                            size="lg"
                            isChecked={localData.autoQuoteStaleOrders}
                            onChange={(e) => handleUpdatePreference('autoQuoteStaleOrders', e.target.checked)}
                            colorScheme="partbay-blue"
                        />
                    }
                />

                <SettingsRow
                    label="Allow scheduled requests"
                    description="When enabled, estimators can schedule quote requests."
                    control={
                        <Switch
                            size="lg"
                            isChecked={localData.allowScheduledRequests}
                            onChange={(e) => handleUpdatePreference('allowScheduledRequests', e.target.checked)}
                            colorScheme="partbay-blue"
                        />
                    }
                />

                <SettingsRow
                    label="Quote cutoff time"
                    description="Quotes requested after this time will be delayed until 8am local time the following business day."
                    control={
                        <HStack spacing={2}>
                            <Select
                                size="sm"
                                w="80px"
                                value={localData.cutoff.hour}
                                onChange={(e) => handleCutoffChange('hour', parseInt(e.target.value))}
                            >
                                {Array.from({ length: 11 }, (_, i) => i + 1).map((h) => (
                                    <option key={h} value={h}>{h}</option>
                                ))}
                            </Select>
                            <Text fontWeight="medium">:</Text>
                            <Select
                                size="sm"
                                w="80px"
                                value={localData.cutoff.minute}
                                onChange={(e) => handleCutoffChange('minute', parseInt(e.target.value))}
                            >
                                {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                                    <option key={m} value={m}>
                                        {m.toString().padStart(2, '0')}
                                    </option>
                                ))}
                            </Select>
                            <Text fontSize="sm" fontWeight="medium" color="gray.600">PM</Text>
                        </HStack>
                    }
                />

                <SettingsRow
                    label="Allow expedited orders"
                    description="Prefers part options with same-day or next-day delivery over margins. Configure when expedited quote requests are allowed as an option."
                    control={
                        <Select
                            size="sm"
                            value={localData.allowExpedited}
                            onChange={(e) => handleUpdatePreference('allowExpedited', e.target.value)}
                            w="200px"
                        >
                            <option value="always">Always</option>
                            <option value="never">Never</option>
                            <option value="last-week">Last Week of Month</option>
                            <option value="last-two-weeks">Last Two Weeks of Month</option>
                        </Select>
                    }
                />

                <SettingsRow
                    label="Allow dealer referral"
                    description="If a dealer referral is provided, the order will be placed only with that dealer, ignoring all others."
                    control={
                        <Switch
                            size="lg"
                            isChecked={localData.allowDealerReferral}
                            onChange={(e) => handleUpdatePreference('allowDealerReferral', e.target.checked)}
                            colorScheme="partbay-blue"
                        />
                    }
                />

                {/* <SettingsRow
                    label="Re-quote incorrect part numbers (CCC only)"
                    description="Automatically corrects incorrect part numbers in the estimate and re-quotes parts. Select when you want this to be enabled."
                    control={
                        // Each mode's rule is specific enough that it needs its own
                        // explanation, so the tooltip hangs off each segment rather
                        // than stacking all three into the row description.
                        <SettingsSegmentedControl
                            segments={REQUOTE_OPTIONS}
                            value={localData.requoteIncorrectPartNumbers}
                            onChange={(value) => handleUpdatePreference('requoteIncorrectPartNumbers', value)}
                        />
                    }
                /> */}
            </SettingsCard>

            <SettingsCard
                title="Re-quote rules"
                description="Configure automatic re-quote rules for specific vendors and makes."
                headerAction={
                    <PartBayButton
                        variant="solid"
                        colorScheme="partbay-blue"
                        onClick={() => navigate('/settings/quoting/new')}
                        text="Add Rule"
                        size="sm"
                    />
                }
            >
                {/* py matches SettingsRow so the table lines up with the rows
                    of the cards above it. */}
                <Box py={4}>
                    <SettingsSubTable
                        isInset
                        currentPage={rulePaging.currentPage}
                        totalPages={rulePaging.totalPages}
                        onPageChange={rulePaging.setCurrentPage}
                        head={
                            <>
                                <SettingsTh w="44px">
                                    <Checkbox
                                        size="sm"
                                        colorScheme="partbay-blue"
                                        isChecked={allPageSelected}
                                        isIndeterminate={!allPageSelected && somePageSelected}
                                        isDisabled={pageIndexes.length === 0}
                                        onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                                        aria-label="Select all rules on this page"
                                    />
                                </SettingsTh>
                                <SettingsTh>Vendor name</SettingsTh>
                                <SettingsTh>Makes</SettingsTh>
                                <SettingsTh>Locations</SettingsTh>
                                <SettingsTh textAlign="center" w="100px">
                                    Actions
                                </SettingsTh>
                            </>
                        }
                    >
                        {ruleRows.length === 0 ? (
                            <Tr>
                                <SettingsTd colSpan={5} py={6} color="gray.500">
                                    No re-quote rules configured. Click "Add Rule" to create one.
                                </SettingsTd>
                            </Tr>
                        ) : (
                            rulePaging.pageItems.map(({ rule, index }) => (
                                // role="group" powers the chevron that fades in
                                // on hover for every editable cell in the row.
                                <Tr key={index} role="group">
                                    <SettingsTd>
                                        <Checkbox
                                            size="sm"
                                            colorScheme="partbay-blue"
                                            isChecked={selectedIndexes.has(index)}
                                            onChange={(e) =>
                                                handleToggleSelect(index, e.target.checked)
                                            }
                                            aria-label={`Select ${rule.name}`}
                                        />
                                    </SettingsTd>
                                    <SettingsTd>
                                        {/* The name is the way into the rule's
                                            own screen; every other column edits
                                            in place. */}
                                        <Text
                                            as="button"
                                            fontSize="sm"
                                            textAlign="left"
                                            color="gray.800"
                                            _hover={{
                                                color: PARTBAY_BLUE,
                                                textDecoration: 'underline',
                                            }}
                                            onClick={() => navigate(`/settings/quoting/${index}`)}
                                        >
                                            {rule.name}
                                        </Text>
                                    </SettingsTd>
                                    <SettingsTd>
                                        <InlineMakesCell
                                            value={rule.makes}
                                            options={oemOptions}
                                            ariaLabel={`Edit ${rule.name} makes`}
                                            onSave={(makes) => handleRuleChange(index, { makes })}
                                        />
                                    </SettingsTd>
                                    <SettingsTd>
                                        <InlineLocationListCell
                                            value={rule.locations}
                                            locations={locations}
                                            ariaLabel={`Edit ${rule.name} locations`}
                                            onSave={(next) =>
                                                handleRuleChange(index, { locations: next })
                                            }
                                        />
                                    </SettingsTd>
                                    <SettingsTd textAlign="center">
                                        <HStack spacing={1} justify="center">
                                            <IconButton
                                                aria-label={`Edit ${rule.name}`}
                                                icon={
                                                    <Icon as={TbEdit as React.ElementType} boxSize={4} />
                                                }
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="gray"
                                                onClick={() =>
                                                    navigate(`/settings/quoting/${index}`)
                                                }
                                            />
                                            <IconButton
                                                aria-label={`Remove ${rule.name}`}
                                                icon={
                                                    <Icon as={TbTrash as React.ElementType} boxSize={4} />
                                                }
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="red"
                                                onClick={() => handleRemoveRule(index)}
                                            />
                                        </HStack>
                                    </SettingsTd>
                                </Tr>
                            ))
                        )}
                    </SettingsSubTable>
                </Box>
            </SettingsCard>

            <BulkActionBar
                selectedCount={selectedIndexes.size}
                actions={BULK_RULE_ACTIONS}
                onClearSelection={() => setSelectedIndexes(new Set())}
                onAction={handleBulkAction}
                // The save/discard pill takes the bottom spot while the section
                // has staged edits, so the two stack rather than overlap.
                bottom={isDirty ? 20 : 4}
            />

            <RequoteBulkEditModal
                field={bulkField}
                selectedCount={selectedIndexes.size}
                locations={locations}
                makeOptions={oemOptions}
                onApply={handleBulkApply}
                onClose={() => setBulkField(null)}
            />

        </VStack>
    );
};

export default Quoting;
